import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router'

import { PATHS } from '@ctcells/core'
import { USER_ROWS, type UserRow } from '@ctcells/fixtures'
import { enumLabel, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { FilterPill } from '../../atoms/FilterPill.tsx'
import { AddModal } from '../../atoms/AddModal.tsx'
import { PasswordField } from '../../atoms/PasswordField.tsx'
import { PasswordInput } from '../../atoms/PasswordInput.tsx'
import { Segmented } from '../../atoms/Segmented.tsx'
import { passwordViolations } from '../../atoms/passwordPolicy.ts'
import { Field } from '../../atoms/Field.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { userStatusTone } from '../../atoms/statusTone.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { gate, viewGate, type ScreenAccess } from '../access.ts'
import { dateTimeText } from '../format.ts'
import { AddActions, FormRow, FormSection, ModalFooter } from '../formSections.tsx'
import { OfflineNotice, RowLink } from '../resourceParts.tsx'
import { GeneralSettings } from './GeneralSettings.tsx'
import { useGroupTabs, type GroupTabDef } from '../groupTabs.ts'
import { withPriority } from '../listColumnPriority.ts'
import { ADMIN_TABS, type AdminTabId } from './adminTabs.ts'

import '../screens.css'
import '../resource.css'

/**
 * 관리 — 와이어프레임 `settings()` 2647행. 일반 · 사용자 **2탭**.
 *
 * 🔴 **세션 탭이 없다.** 사용자 상세로 흡수됐다(§1-4). 내 계정의 세션 구역은 별개로 살아 있다.
 * 🔴 **글자 아바타 · 이니셜 칩 전면 금지**(§1-2). 사람 화면도 예외가 아니다.
 * 🔴 **목록은 읽기 전용이다.** 역할 변경 같은 것은 사용자 상세에서만 한다(§14-4).
 * 🔴 사용자 추가 모달에 **부제를 달지 않았다**(§1-1 · eric 지시).
 */
export type AdminTab = 'general' | 'webhooks' | 'members'

export interface AdminScreenProps {
  /**
   * 지금 열린 탭. 🔴 **라우트가 넘긴다** — 화면이 상태로 들고 있지 않는다(탭 = 주소).
   * 기본값은 첫 탭(`members`)이라 prop 없이 쓰던 시험·스토리가 그대로 돈다.
   */
  tab?: AdminTab
  /**
   * 탭 줄에 실을 목록. 🔴 **관리는 이 무리의 «본거지»라 줄이 항상 있다** — 손님으로 얹히는
   * 화면들(모듈·로그·시료·작업 …)은 prop 을 안 넘기면 줄이 사라지지만, 여기서 줄을 없애면
   * 사용자 ↔ 일반을 오갈 길이 없어진다. 그래서 «끄기»가 아니라 «갈아 끼우기»다.
   *
   * 기본값은 넷(사용자·일반·모듈·로그) — 시료 관점 콘솔이 모듈·로그를 관리 안으로 넣었다.
   * 장비 관점 콘솔은 그 둘이 사이드바에 제 항목으로 있어서 **둘짜리 목록**을 넘긴다
   * (`apps/console-equipment` 의 `EQUIPMENT_ADMIN_TABS`).
   */
  adminTabs?: readonly GroupTabDef<AdminTabId>[]
  /**
   * 설정 탭의 내용 — **컨테이너가 넣는 슬롯**(2026-10-28 «알람 설정»). 이 화면은 표시 전용이라 조회 · 구독을 직접 하지 않는다.
   * 없으면 탭만 선다(장비 관점 콘솔 · 단독 렌더).
   */
  settings?: ReactNode
  users?: readonly UserRow[]
  /**
   * 오른쪽 판에 열린 계정 — 그 행을 «고른 행»으로 그린다(eric 2026-09-22 「row 를 클릭하면
   * 상세 페이지 내용이 보이게」). 🔴 **값은 `username` 이다** — 주소(`/admin/members/:userId`)와
   * 표 링크(`RowLink`)가 둘 다 `username` 을 쓴다(`user_id` 는 UUID·문자열이 섞여 있다).
   * ⚠ `members` 탭에서만 뜻이 있다. 다른 탭은 이 표 자체가 없다.
   */
  selectedUserId?: string | undefined
  access?: ScreenAccess
  loading?: boolean
  /**
   * 사용자 목록을 **못 본 까닭**(조회 실패 문구). 🔴 **사용자 표 «안»에만 선다** — 탭 줄과 다른 탭은 그대로 산다.
   * 종전에는 컨테이너가 이 실패로 화면 전체를 한 줄로 갈아 끼워 모듈 · 로그 탭으로 갈 길까지 없앴다(2026-09-30).
   */
  usersNotice?: string | undefined
  /**
   * 계정 등록. 🔴 **모양이 계약 본문이다** — `UserCreateEnvelope`(실서버 대조 2026-09-18)의
   * 필수는 `command_id`·`username`·`name`·`role`·`password` 다.
   *
   * ⚠ **이메일은 싣지 않는다.** 화면은 받지만 `User` 스키마에 그 필드가 **없다**(정본·실서버 둘 다).
   *   보내도 서버가 버린다 — 있는 척 넘기지 않는다(팀장 보고 대상).
   * ⚠ 역할 `service` 는 서버가 모르는 값이라 여기로 오지 않는다. 계약 enum 3종뿐이다.
   */
  onCreate?: ((input: CreateUserInput) => void) | undefined
  creating?: boolean | undefined
  createError?: string | undefined
}

/**
 * 계정 상세로 가는 곳. 🔴 **첫 칸 링크와 행 클릭이 같은 함수를 쓴다** — 경로 문자열을 두 벌
 * 적으면 한쪽만 고쳐져 누르는 자리마다 다른 데로 간다.
 * ⚠ 열쇠는 `username` 이다 — 주소도 표도 그것을 쓴다(`user_id` 는 UUID·문자열이 섞여 있다).
 */
const memberDetailPath = (user: UserRow): string => `${PATHS.admin}/members/${user.username}`

/** 계약 enum 3종. `service` 는 화면 어휘이고 서버 어휘가 아니다 */
export type WritableRole = 'admin' | 'operator' | 'viewer'

export interface CreateUserInput {
  username: string
  name: string
  role: WritableRole
  password: string
}

/** 사용자 표의 처음 정렬 — 계정 ID 오름차순(서버 차례) */
const MEMBERS_DEFAULT_SORT: TableSort = { key: 'username', direction: 'asc' }

/** 역할 권한 차례(오름차순 기준). 모르는 역할(`service` 등)은 가장 낮게 본다 */
const ROLE_RANK: Readonly<Record<string, number>> = { viewer: 0, operator: 1, admin: 2 }
const roleRank = (role: string): number => ROLE_RANK[role] ?? -1

export function AdminScreen({
  tab = 'members',
  adminTabs: tabDefs = ADMIN_TABS,
  settings,
  users = USER_ROWS,
  selectedUserId,
  access = {},
  loading = false,
  usersNotice,
  onCreate,
  creating,
  createError,
}: AdminScreenProps) {
  // 🔴 **탭은 주소가 정한다**(2026-09-18). 화면이 상태로 들고 있지 않는다 — 그래야 새로고침과
  //    「← 사용자」 복귀에서 탭이 안 흔들린다. 어느 탭인지는 라우트가 prop 으로 넘긴다.
  const adminTabs = useGroupTabs(tabDefs, tab, access)
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [role, setRole] = useState<UserRow['role']>('operator')
  /*
   * 🔴 **입력 검사는 «추가»를 누를 때다**(eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」). 종전에는 빈 칸 · 정책 미달 ·
   *    확인 불일치를 `reason.commandNotAllowed` 비활성으로 막아 무엇이 빠졌는지 안 보였다. 권한 · 전송 중은 그대로 `createGate`.
   *    라벨↔입력 id 는 훅이 준다(`field(key)`)
   */
  const userForm = useSubmitValidation(validateNewUser)
  const accountField = userForm.field('accountId')
  const nameField = userForm.field('userName')
  const passwordField = userForm.field('password')
  const confirmField = userForm.field('confirm')
  const roleField = userForm.field('role')
  const draft: NewUserDraft = { accountId, userName, password, passwordConfirm, role }

  const settingsGate = gate(access, { perm: 'settings' })
  /*
   * 🔴 **목록을 «볼» 수 있는가는 읽기 게이트다**(`viewGate`). 쓰기 게이트(`settingsGate`)로 막으면 구독이 끊겼을 때
   *    admin 의 읽기 전용 표까지 「연결 끊김」 한 줄로 사라진다 — 끊김은 버튼을 막는 것이지 목록을 가리는 게 아니다.
   * ⚠ 권한이 없으면 사유는 「admin 만」이다 — `GET /users` 는 admin 전용(`users.py ALLOWED_ROLES`)이라 그 사실을 적는다.
   */
  const listDenied = viewGate(access, 'settings').disabled
  const membersNotice = listDenied ? t('reason.usersAdminOnly') : usersNotice

  const needle = query.trim().toLowerCase()
  const rows = users.filter(
    (user) =>
      needle === '' ||
      user.username.toLowerCase().includes(needle) ||
      user.name.toLowerCase().includes(needle),
  )

  /*
   * 🔴 **열은 다섯이다** — 계정 ID · 이름 · 역할 · 상태 · 마지막 로그인(eric 2026-09-30). 「세션」(사용자당 세션 수)
   *    열은 뺐다. 그 열 때문에 목록이 `GET /sessions` 를 같이 불렀는데 그 조회도 같이 걷었다(`AdminContainer`).
   * 🔴 **폭 배분**(eric 2026-09-30 「해당 컬럼 크기랑 여백 고려해서 전체 컬럼 위치를 조절」): 다섯 열 모두 `fit` 이다.
   *    칸이 전부 같은 퍼센트(`1%`)라 브라우저가 남는 폭을 **똑같이** 나눈다 — 칸마다 «내용 폭 + 같은 여백»이 되어
   *    열 사이가 고르게 선다(1440 실측: 다섯 열 모두 +127px). `fill` 을 이름에 주면 이름 뒤만 크게 비었고
   *    (이름이 841px), 자동 배분은 내용 폭에 비례해 긴 계정 ID 열이 제일 많이 받았다.
   * 🔴 **서랍이 열려 있으면 이름 열만 폭 옵션을 뺀다**(접힐 수 있게). `fit` 은 한 줄이라 이름 최소 폭이 가장 긴
   *    이름 전체(약 205px)가 되고, 1280 서랍(상자 476px)에서 이름 열이 통째로 빠졌다. 접히게 두면 계정 ID · 이름 ·
   *    역할 · 상태가 선다. 좁은 상자라 남는 폭이 작아 «이름 뒤만 비는» 모양도 안 생긴다.
   * 머리와 값은 전부 왼쪽 정렬이다(`align` 을 안 준다).
   */
  const drawerOpen = selectedUserId !== undefined
  const columns: Column<UserRow>[] = [
    {
      key: 'username',
      header: t('column.accountId'),
      width: 'fit',
      sort: 'text',
      sortValue: (user) => user.username,
      cell: (user) => <RowLink to={memberDetailPath(user)}>{user.username}</RowLink>,
    },
    {
      /*
       * 🔴 **예전에는 「이메일」 열이었다.** `User` 스키마에 그 필드가 «없어»(정본·실서버 둘 다
       *    실측 2026-09-20) 실물에서는 항상 `—` 였고, 화면은 fixture 값을 보여주고 있었다.
       *    `name` 은 계약에 실재한다 — 되돌리지 마라.
       */
      key: 'name',
      header: t('column.name'),
      ...(drawerOpen ? {} : { width: 'fit' as const }),
      sort: 'text',
      sortValue: (user) => user.name,
      cell: (user) => <span className="muted">{user.name}</span>,
    },
    {
      key: 'role',
      header: t('column.role'),
      width: 'fit',
      // 🔴 권한 순(viewer < operator < admin) — 글자 순이면 admin 이 맨 앞에 선다. 내림차순이 admin 부터다
      sort: (a, b) => roleRank(a.role) - roleRank(b.role),
      cell: (user) => user.role,
    },
    {
      key: 'status',
      header: t('column.status'),
      width: 'fit',
      sort: 'text',
      sortValue: (user) => enumLabel('enum.userStatus', user.status),
      /*
       * 🔴 **잠김이면 해제 시각을 같이 보인다.** `User.locked_until` 이 응답에 있는데 표가 안
       *    쓰고 있었다 — 「잠김」만 보면 언제 풀리는지 알 길이 없어 관리자가 손으로 풀게 된다.
       * ⚠ **해제 시각이 지나도 상태는 `locked` 로 남는다** — 해제는 경과 뒤 «첫 로그인 시도»에
       *   일어난다(서버 판정). 그래서 화면이 시계로 「이미 풀렸다」를 계산하지 않는다(DP-04).
       */
      cell: (user) => (
        <>
          <StatusBadge
            /* 테 없는 알약 — 작업 준비 · 알람 심각도 칩과 같은 규칙(eric 2026-10-30 「관리 › 사용자 쪽도 테두리 없게」) · 색 · 글자 그대로 */
            flat
            label={enumLabel('enum.userStatus', user.status)}
            status={user.status === 'active' ? 'IDLE' : 'STOPPED'}
            /* 시안 알약 색 — 활성 초록 · 잠김 호박 · 비활성 중성(eric 2026-10-12 「사용자에서 상태.. 디자인팀의 색상으로」) */
            tone={userStatusTone(user.status)}
          />
          {user.status === 'locked' && user.locked_until ? (
            <span className="muted" title={t('action.lock')}>
              {' '}
              {dateTimeText(user.locked_until)}
            </span>
          ) : null}
        </>
      ),
    },
    {
      // ⚠ 키 `lastSeen` 은 열 차례표(`listColumnPriority.ts`)가 쓰는 이름이라 그대로 둔다 — 뜻은 머리글이 말한다
      key: 'lastSeen',
      header: t('column.lastLogin'),
      width: 'fit',
      // 정렬은 원값(`last_login_at`)으로 — 표기(`last_seen_text`)는 글자라 시각 순이 아니다. 없으면 맨 뒤
      sort: 'time',
      sortValue: (user) => user.last_login_at,
      // 🔴 값은 `User.last_login_at` 이다 — 머리글도 「마지막 로그인」(eric 2026-09-30 「마지막 로그인으로 수정해」).
      //    세션의 `last_seen_at`(진짜 마지막 활동)은 상세의 세션 표가 「마지막 활동」으로 보인다
      cell: (user) => <span className="muted">{user.last_seen_text}</span>,
    },
  ]

  /*
   * 주 버튼은 권한 · stale · 전송 중만 막는다. 계약 필수값(`username`·`name`·`role`·`password` + 확인 일치 · 정책)은
   * 누를 때 `validateNewUser` 가 재고 빠진 칸 아래에 말한다.
   */
  const createGate =
    creating === true
      ? ({ disabled: true, disabledReason: t('reason.actionInFlight') } as const)
      : gate(access, { perm: 'settings' })

  return (
    <ListPage
      onTabChange={adminTabs.onChange}
      tabValue={adminTabs.value}
      tabs={adminTabs.tabs}
      tabsLabel={t('screen.admin.title')}
      title={t('screen.admin.title')}
      {...(tab === 'members'
        ? {
            primaryAction: (
              <Button
                size="pill"
                variant="primary"
                {...settingsGate}
                onClick={() => {
                  userForm.reset()
                  setAddOpen(true)
                }}
              >
                {t('action.addUser')}
              </Button>
            ),
            toolbar: (
              <Toolbar label={t('tab.admin.members')}>
                <SearchInput
                  onChange={setQuery}
                  placeholder={t('filter.search.users')}
                  value={query}
                />
                <FilterPill>{t('filter.status')}</FilterPill>
              </Toolbar>
            ),
          }
        : {})}
    >
      <OfflineNotice shown={access.offline === true} />

      {/*
        🔴 **권한이 없으면 사용자 탭의 내용을 내주지 않는다**(2026-09-18).
           모듈·로그가 관리 안으로 들어오면서 사이드바 문이 `monitor` 로 내려갔다 — 그래서
           `settings` 없는 사용자도 이 화면에 **들어올 수는 있다.** 탭만 막고 주소로 들어오면
           내용이 보이면 막은 것이 아니다. 탭 줄은 남긴다 — 모듈·로그 탭으로 갈 길이 필요하다.
           사유는 표 «안»의 알림 한 줄이다(`membersNotice` · 2026-09-30). 행은 넘기지 않는다.
        ⚠ **설정 탭은 여기서 갈린다**(2026-09-22) — 그 탭은 절마다 권한이 달라져서
          통째로 막지 않는다. 아래 분기의 첫 갈래가 그 까닭을 적었다.
        ⚠ **집행은 서버다.** 이건 노출 판단일 뿐이고, 목록·설정은 서버가 403
           `INSUFFICIENT_ROLE` 로 막는다. 여기서 막았다고 안전해지는 것이 아니다.
      */}
      {tab === 'general' || tab === 'webhooks' ? (
        /* 🔴 설정 탭의 내용은 컨테이너가 넣는 슬롯 하나(«알람 설정» · eric 2026-10-28 · `GeneralSettings` 머리말) */
        <GeneralSettings>{settings}</GeneralSettings>
      ) : (
        <DataTable
          caption={t('tab.admin.members')}
          /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
          columns={withPriority('members', columns)}
          /*
           * 처음 차례는 계정 ID 오름차순 — 서버가 주는 차례와 같다(`views/users.py` `order_by(username)`).
           * 머리글 정렬은 받은 줄(`PAGE_LIMIT` · `AdminContainer`) 안에서다 — `GET /users` 에 정렬 인자가 없다.
           */
          defaultSort={MEMBERS_DEFAULT_SORT}
          emptyLabel={t('state.empty.users')}
          loading={loading}
          /*
           * 🔴 **권한 없음 · 조회 실패는 표 «안»에 선다**(2026-09-30). 탭 줄 · 다른 탭은 그대로다.
           *    권한이 없으면 행을 아예 넘기지 않는다(demo 기본값 `USER_ROWS` 도 새지 않게).
           */
          notice={membersNotice}
          rows={listDenied ? [] : rows}
          /*
           * 🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23). 첫 칸 `RowLink` 는 그대로다 —
           *    키보드로 가는 길은 그 버튼뿐이다(`DataTable.onRowClick` 주석).
           */
          onRowClick={(user) => void navigate(memberDetailPath(user))}
          rowKey={(user) => user.username}
          selected={(user) => user.username === selectedUserId}
        />
      )}

      {addOpen ? (
        /*
          🔴 **추가 창 일곱의 한 틀**(`AddModal` · eric 2026-09-30 「창마다 포맷이 달라.. 다 통일해줘」). 종전 기본 창(560 ·
             라벨 왼쪽 · 덮개가 화면 전체라 E-STOP 을 가림 · 「계정 만들기」)을 걷었다. 칸 · 검증 · 요청 본문은 그대로다.
        */
        <AddModal
          size="md"
          busy={creating === true}
          onClose={() => setAddOpen(false)}
          title={t('screen.userNew.title')}
        >
          <FormSection>
            <FormRow>
              <Field label={t('field.accountId')} required {...accountField.fieldProps}>
                <TextInput
                  {...accountField.inputProps}
                  onChange={(value) => {
                    setAccountId(value)
                    userForm.change('accountId', { ...draft, accountId: value })
                  }}
                  value={accountId}
                  wide
                  {...settingsGate}
                />
              </Field>
              <Field label={t('field.userName')} required {...nameField.fieldProps}>
                <TextInput
                  {...nameField.inputProps}
                  onChange={(value) => {
                    setUserName(value)
                    userForm.change('userName', { ...draft, userName: value })
                  }}
                  value={userName}
                  wide
                  {...settingsGate}
                />
              </Field>
            </FormRow>
            {/* 비밀번호 · 확인은 한 쌍이다 — 넓은 창(eric 2026-09-30 「너무 좁아」)에서 한 줄에 나란히 */}
            <FormRow>
              <PasswordField
                error={passwordField.fieldProps.error}
                id={passwordField.inputProps.id}
                label={t('field.password')}
                onChange={(value) => {
                  setPassword(value)
                  userForm.change('password', { ...draft, password: value }, ['confirm'])
                }}
                required
                validation="submit"
                value={password}
              />
              <Field label={t('field.passwordConfirm')} required {...confirmField.fieldProps}>
                <PasswordInput
                  {...confirmField.inputProps}
                  onChange={(value) => {
                    setPasswordConfirm(value)
                    userForm.change('confirm', { ...draft, passwordConfirm: value })
                  }}
                  value={passwordConfirm}
                  wide
                />
              </Field>
            </FormRow>
            {/*
              🔴 **이메일 칸을 되살리지 마라**(2026-09-20 제거). `User` 스키마에 그 필드가 «없어»
                 등록 본문에 실을 수 없었다 — 사용자가 채워도 버려지는 칸이었다. 와이어프레임에
                 있다는 것은 근거가 아니다(우선순위: eric 지시 > API 스펙 > 와이어프레임).
            */}
            {/* 역할 — 고를 것 넷 · 짧은 글자 → 구획 선택기(추가 창 규격). 선택지는 종전 그대로다 */}
            <Field label={t('column.role')} required {...roleField.fieldProps}>
              <Segmented
                {...roleField.inputProps}
                label={t('column.role')}
                onChange={(value) => {
                  setRole(value)
                  userForm.change('role', { ...draft, role: value })
                }}
                options={(['admin', 'operator', 'service', 'viewer'] as const).map((key) => ({
                  value: key,
                  label: key,
                }))}
                value={role}
                {...settingsGate}
              />
            </Field>
          </FormSection>
          <ModalFooter>
            <AddActions
              busy={creating === true}
              error={createError}
              framed
              onCancel={() => setAddOpen(false)}
              onSubmit={() => {
                if (createGate.disabled) return
                // 창 바닥 줄은 폼 밖(포털)이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
                if (!userForm.validate(draft, document.body) || !isWritableRole(role)) return
                onCreate?.({
                  username: accountId.trim(),
                  name: userName.trim(),
                  role,
                  password,
                })
              }}
              pageLabel={t('action.add')}
              submitGate={createGate}
            />
          </ModalFooter>
        </AddModal>
      ) : null}
    </ListPage>
  )
}

type NewUserDraft = {
  accountId: string
  userName: string
  password: string
  passwordConfirm: string
  role: string
}

/**
 * 사용자 추가 — 누를 때 재는 입력 규칙. 🔴 `service` 는 서버가 모르는 역할이라 **보낼 수 없는 값**이다(BR-42).
 * 정책 문구는 위반했을 때만 칸 아래에 선다(§1-1).
 */
function validateNewUser(d: NewUserDraft) {
  const violations = passwordViolations(d.password)
  return {
    accountId: d.accountId.trim() === '' ? t('validation.accountIdRequired') : undefined,
    userName: d.userName.trim() === '' ? t('validation.userNameRequired') : undefined,
    password:
      d.password === ''
        ? t('validation.password.required')
        : violations.length > 0
          ? violations.join(' · ')
          : undefined,
    confirm:
      d.passwordConfirm === ''
        ? t('validation.password.confirmRequired')
        : d.passwordConfirm !== d.password
          ? t('validation.password.mismatch')
          : undefined,
    role: isWritableRole(d.role) ? undefined : t('validation.roleRequired'),
  }
}

/** 계약 enum 3종. `service` 는 화면 어휘이고 서버 어휘가 아니다 */
function isWritableRole(role: string): role is WritableRole {
  return role === 'admin' || role === 'operator' || role === 'viewer'
}
